import type {
  TesterAvailability,
  TesterDevice,
  PublicTesterProfile,
} from '@runhuman/shared';
import { deriveTesterColor, formatLastSeen, DEVICE_META, sortedDevices } from '@runhuman/shared';
import { FloorManagerBadge } from '../../FloorManagerBadge';

// `on_break` is a stored availability that the auto-presence sweep writes
// when an approved-break window covers "now". User-visible surfaces alias
// it to `away` (#3667), matching the web's InternalTesterProfileDialog.
// `invisible` (#4427) is admin-only — they're filtered out of presence, so
// this modal rarely renders for an invisible tester; surfaced literally
// when it does so the admin sees their real state.
const AVAILABILITY_LABEL: Record<TesterAvailability, string> = {
  online: 'Online',
  away: 'Away',
  offline: 'Offline',
  on_break: 'Away',
  invisible: 'Invisible',
};

interface HeaderProps {
  readonly profile: PublicTesterProfile;
  /** Live availability from the presence row — overrides the slower-moving profile field. */
  readonly liveAvailability: TesterAvailability;
}

/**
 * Activity-side mirror of `<InternalTesterProfileDialog>`'s header. Same
 * information density (avatar, alias, badges, availability + last-seen)
 * with class names that map to the Activity's `profile-modal__*` CSS — the
 * web's inline `theme.*` tokens are replaced by CSS custom properties so
 * any future token changes only need to be edited once in `tokens.css`.
 */
export function Header({ profile, liveAvailability }: HeaderProps) {
  const lastSeen = formatLastSeen(profile.lastActiveAt);
  const hasBadges = profile.isAdmin || profile.isFloorManager || profile.devices.length > 0;
  const color = deriveTesterColor(profile.alias);
  return (
    <div className="profile-modal__header">
      {profile.avatarUrl ? (
        <img
          src={profile.avatarUrl}
          alt=""
          className="profile-modal__avatar"
          style={{ borderColor: color }}
        />
      ) : (
        <div
          className="profile-modal__avatar profile-modal__avatar--fallback"
          style={{ background: color }}
        >
          {profile.alias[0]?.toUpperCase()}
        </div>
      )}
      <div className="profile-modal__header-text">
        <h2 className="profile-modal__alias">{profile.alias}</h2>
        {hasBadges && (
          <div className="profile-modal__badges">
            {profile.isAdmin && <AdminBadge />}
            {profile.isFloorManager && <FloorManagerBadge variant="full" />}
            {sortedDevices(profile.devices).map((device) => (
              <DeviceBadge key={device} device={device} />
            ))}
          </div>
        )}
        <div className="profile-modal__meta">
          <span className={`profile-modal__avail profile-modal__avail--${liveAvailability}`}>
            <span className={`profile-modal__avail-dot profile-modal__avail-dot--${liveAvailability}`} />
            {AVAILABILITY_LABEL[liveAvailability]}
          </span>
          <span className="profile-modal__sep">·</span>
          <span className="profile-modal__last-seen">Last seen {lastSeen}</span>
        </div>
      </div>
    </div>
  );
}

function AdminBadge() {
  return (
    <span className="profile-modal__badge profile-modal__badge--admin" title="Runhuman admin">
      <i className="fa-solid fa-shield-halved" aria-hidden="true" />
      Admin
    </span>
  );
}

function DeviceBadge({ device }: { device: TesterDevice }) {
  const meta = DEVICE_META[device];
  return (
    <span
      className="profile-modal__badge profile-modal__badge--device"
      aria-label={meta.label}
      title={meta.label}
    >
      <i className={meta.iconClass} aria-hidden="true" />
    </span>
  );
}
